/* eslint-disable @typescript-eslint/strict-boolean-expressions */
/* eslint-disable @typescript-eslint/no-floating-promises */
/* eslint-disable @typescript-eslint/explicit-function-return-type */
/* eslint-disable @typescript-eslint/no-unused-vars */
/*
 * https://kentcdodds.com/blog/authentication-in-react-applications
 * https://www.digitalocean.com/community/tutorials/react-manage-user-login-react-context
 */
import { type CWEnv, initCWElements } from "@cw-elements/config";
import queryString from "query-string";
import React, { useEffect, useMemo, useState } from "react";
import { rememberProductEntry } from "shared/utils/productEntry";
import { Loader } from "../../shared/components/Loader";
import { getAccessToken } from "../../shared/utils/axios";
import { authService } from "./AuthService";

interface AuthInterface {
  user?: any;
  logoutStatus?: boolean;
  error?: any;
  setLogoutStatus?: any;
  status?: string;
  hasLicenceAccess: boolean;
}

const AUTH_META: AuthInterface = {
  status: "idle",
  error: null,
  user: null,
  hasLicenceAccess: false,
};

const AuthContext = React.createContext<AuthInterface>(AUTH_META);
let isApiCalled = false;

export function AuthProvider(props: any) {
  const [logoutStatus, setLogoutStatus] = useState<boolean>(false);
  const [authStatus, setAuthStatus] = useState<{
    status: string;
    errorMsg: string;
  }>({ status: "idle", errorMsg: "" });
  const [userFetchError, setUserFetchError] = useState<string>();
  const [user, setUser] = useState<any>({});
  const [hasLicenceAccess, setHasLicenceAccess] = useState<boolean>(true);

  const login = async (code: string) => {
    try {
      setAuthStatus({ status: "loading", errorMsg: "" });
      const token = await authService.performLogin(code);
      if (token) {
        await getCurrentUser();
      }
    } catch (error) {
      console.error("Login error:", error);
      setAuthStatus({ status: "error", errorMsg: "Something Went Wrong" });
    }
  };

  const getCurrentUser = async (retries = 3): Promise<void> => {
    try {
      const result = await authService.currentUser();
      if (!result) {
        throw new Error("No result from currentUser");
      }
      const { user: fetchedUser, hasLicenceAccess: licenceAccess } = result;
      setHasLicenceAccess(Boolean(licenceAccess));
      if (fetchedUser && Object.keys(fetchedUser).length > 0) {
        initCWElements({
          environment: process.env.REACT_APP_CW_ELEMENTS_ENVIRONMENT as CWEnv,
          token: getAccessToken(),
          hostAppId: process.env.REACT_APP_ID,
        });
        setUser(fetchedUser);
        setAuthStatus({ status: "success", errorMsg: "" });
      } else if (retries > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, 1500));
        await getCurrentUser(retries - 1);
      } else {
        setAuthStatus({ status: "", errorMsg: "" });
        setUserFetchError("Error in Fetching User");
      }
    } catch (err) {
      if (retries > 0) {
        await new Promise<void>((resolve) => setTimeout(resolve, 1500));
        await getCurrentUser(retries - 1);
      } else {
        console.error("getCurrentUser error after exhausting retries:", err);
        setAuthStatus({ status: "error", errorMsg: "Something Went Wrong" });
      }
    }
  };

  const query = queryString.parse(globalThis.location.search);

  useEffect(() => {
    let active = true;
    if (Boolean(query?.code) && !isApiCalled) {
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      if (!sessionStorage.getItem(`cod${query.code}`)) {
        isApiCalled = true;
        login(query.code as string);
      }
    } else if (authService.isAccessTokenAvailable()) {
      getCurrentUser();
    } else {
      rememberProductEntry(globalThis.location);
      const startSignIn = async (): Promise<void> => {
        try {
          const url = await authService.getOAuthUrl();
          if (active) globalThis.location.href = url;
        } catch {
          if (active) {
            setAuthStatus({
              status: "error",
              errorMsg: "Unable to start secure sign-in.",
            });
          }
        }
      };
      void startSignIn();
    }
    return () => {
      active = false;
    };
  }, []);

  const { status, errorMsg } = authStatus;

  const authContextProviderValue = useMemo(
    () => ({
      status,
      logoutStatus,
      setLogoutStatus,
      user,
      error: errorMsg || userFetchError,
      hasLicenceAccess,
    }),
    [
      status,
      logoutStatus,
      setLogoutStatus,
      user,
      errorMsg,
      userFetchError,
      hasLicenceAccess,
    ]
  );

  return (
    <AuthContext.Provider value={authContextProviderValue}>
      {["loading", "idle"].includes(status) ? (
        <Loader mask={true} message={`${"Authenticating..."}`} />
      ) : (
        <>
          {userFetchError ?? (status === "error" && errorMsg) ? (
            <Loader mask={true} message="Error..." />
          ) : (
            props.children
          )}
        </>
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  const isPending = ["loading", "idle"].includes(context.status ?? "");
  const isError = context.status === "error";
  const isSuccess = context.status === "success";
  const isAuthenticated = Boolean(context.user) && isSuccess;
  return {
    ...context,
    logout: authService.logout,
    logoutStatus: context.logoutStatus,
    setLogoutStatus: context.setLogoutStatus,
    isPending,
    isSuccess,
    isError,
    isAuthenticated,
  };
}
